import React, { useState } from "react";
import { 
  Box, 
  IconButton, 
  Typography,
  Link,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import DocumentModal from "./DocumentModal";
import dayjs from "dayjs";

const deliveryLaydownColumns = [
  { key: 1, value: "date", hidden: false },
  { key: 2, value: "time", hidden: false },
  { key: 3, value: "name", hidden: false },
  { key: 4, value: "comments", hidden: false },
  { key: 5, value: "documents", hidden: false },
  { key: 6, value: "actions", hidden: false },
];

const convertFromTimestamp = (timestamp) => {
  const date = dayjs.unix(timestamp);
  return {
    date: date.format('YYYY-MM-DD'),
    time: date.format('HH:mm'),
  };
};

const generateDataGridColumns = (columns, onEdit, onDelete, aliasName, onDocumentClick) =>
  columns
    .filter((col) => !col.hidden)
    .map((col) => {
      switch (col.value) {
        case "date":
          return {
            field: "date",
            headerName: "Date",
            flex: 2,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) =>
              params.row.date_time
                ? convertFromTimestamp(params.row.date_time).date
                : "-",
          };

        case "time":
          return {
            field: "time",
            headerName: "Time",
            flex: 2,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) =>
              params?.row.date_time
                ? convertFromTimestamp(params?.row?.date_time).time
                : "-",
          };

        case "name":
          return {
            field: "name",
            headerName: aliasName || "Name",
            flex: 2,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
          };

        case "comments":
          return {
            field: "comments",
            headerName: "Comments",
            flex: 3,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
          };

        case "documents":
          return {
            field: "documents",
            headerName: "Documents",
            flex: 3,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
              const documents = params.row.documents || [];
              
              if (!documents || documents.length === 0) {
                return (
                  <Typography variant="body2" color="textSecondary">
                    No documents
                  </Typography>
                );
              }

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}>
                  {documents.slice(0, 2).map((doc, index) => (
                    <Link
                      key={index}
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click
                        onDocumentClick(doc);
                      }}
                      sx={{
                        textAlign: 'left',
                        textDecoration: 'underline',
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    >
                      {doc.name || `Document ${doc.upload_id}`}
                    </Link>
                  ))}
                  {documents.length > 2 && (
                    <Typography variant="caption" color="textSecondary">
                      +{documents.length - 2} more
                    </Typography>
                  )}
                </Box>
              );
            },
          };

        case "actions":
          return {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    onDelete(params?.row);
                  }}
                  sx={{ color: "red" }}
                >
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ),
          };

        default:
          return {
            field: col.value,
            headerName:
              col.value.charAt(0).toUpperCase() + col.value.slice(1),
            flex: 2,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
          };
      }
    });

const TableComponent = ({ data, handleEdit, handleDelete, aliasName, loader }) => {
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const handleRowClick = (params) => {
    handleEdit(params?.row);
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setDocumentModalOpen(true);
  };

  const handleDocumentModalClose = () => {
    setDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  const dataGridColumns = generateDataGridColumns(
    deliveryLaydownColumns,
    handleEdit,
    handleDelete,
    aliasName,
    handleDocumentClick
  );

  return (
    <Box sx={{ height: "calc(100vh - 303px)", width: "100%" }}>
      <DataGrid
        rows={data?.delivery?.map((row, index) => ({
          id: index,
          ...row,
        }))}
        loading={loader}
        columns={dataGridColumns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        sx={{
          "& .MuiDataGrid-columnSeparator": {
            color: "#fff",
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            backgroundColor: "#f16253",
            color: "#ffffff",
          },
          "& .MuiDataGrid-columnGroupHeader": {
            backgroundColor: "#f16253",
            color: "#ffffff",
            fontWeight: 600,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            width: "100%",
          },
          "& .header-group": {
            display: "flex",
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      />

      <DocumentModal
        open={documentModalOpen}
        onClose={handleDocumentModalClose}
        document={selectedDocument}
      />
    </Box>
  );
};

export default TableComponent;